import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import useApi from "../../hooks/api/axiosInterceptor"; // Using your existing API hook

const Wrapper = styled.div`
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
    padding: 2rem;
`;

const FormTitle = styled.h2`
    font-size: 24px;
    margin-bottom: 30px;
    color: #333;
    text-align: center;
`;

const FormTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 30px;
`;

const TableRow = styled.tr`
    border-bottom: 1px solid #e0e0e0;
    
    &:first-child {
        border-top: 2px solid #666;
    }
`;

const TableHeader = styled.th`
    width: 30%;
    padding: 15px 10px;
    background-color: #f9f9f9;
    text-align: left;
    font-weight: 600;
`;

const TableCell = styled.td`
    padding: 15px 10px;
`;

const Input = styled.input`
    padding: 8px 12px;
    border: 1px solid #ddd;
    width: ${props => props.width || "100%"};
    max-width: ${props => props.maxWidth || "none"};
`;

const AddressButton = styled.button`
    padding: 8px 15px;
    background-color: #2056ad;
    color: white;
    border: none;
    margin-left: 5px;
    cursor: pointer;
    
    &:hover {
        background-color: #1a4786;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 40px;
    margin-bottom: 40px;
`;

const SubmitButton = styled.button`
    width: 100px;
    padding: 12px 25px;
    background-color: #2056ad;
    color: white;
    border: none;
    cursor: pointer;
    font-weight: 500;
    
    &:hover {
        background-color: #1a4786;
    }
`;

const CancelButton = styled.button`
    width: 100px;
    padding: 12px 25px;
    background-color: #6c757d;
    color: white;
    border: none;
    cursor: pointer;
    font-weight: 500;
    
    &:hover {
        background-color: #5a6268;
    }
`;

const ErrorMessage = styled.div`
    color: #e74c3c;
    font-size: 14px;
    margin-top: 5px;
`;

const PhoneContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const HelpText = styled.span`
    font-size: 14px;
    color: #888;
    margin-left: 8px;
`;

// 다음 우편번호 검색 모달
const PostcodeModal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const PostcodeContainer = styled.div`
    position: relative;
    width: 500px;
    height: 500px;
    background-color: white;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    overflow: hidden;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 10px;
    right: 10px;
    width: 30px;
    height: 30px;
    border: none;
    background-color: rgb(110, 110, 110);
    color: white;
    border-radius: 50%;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 1001;
    
    &:hover {
        background-color: rgb(75, 75, 75);
    }
`;

const TermsContainer = styled.div`
    margin-bottom: 20px;
    border: 1px solid #ddd;
    padding: 15px;
    max-height: 200px;
    overflow-y: auto;
`;

const TermsCheckbox = styled.div`
    display: flex;
    align-items: center;
    margin-top: 10px;
    
    input {
        margin-right: 10px;
    }
`;

const Signup = () => {
    const [formData, setFormData] = useState({
        name: '',
        userId: '',
        password: '',
        confirmPassword: '',
        zipCode: '',
        address1: '',
        address2: '',
        phone1: '',
        phone2: '',
        phone3: '',
        email: '',
        backupEmail: ''
    });

    const [errors, setErrors] = useState({});
    const [showPostcode, setShowPostcode] = useState(false);
    const [emailVerificationCode, setEmailVerificationCode] = useState('');
    const [isEmailVerified, setIsEmailVerified] = useState(false);
    const postcodeContainerRef = useRef(null);
    const navigate = useNavigate();
    const [verifyButtonEnabled, setVerifyButtonEnabled] = useState(false);

    const loadDaumPostcodeScript = () => {
        if (!document.getElementById('daum-postcode-script')) {
            const script = document.createElement('script');
            script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
            script.id = 'daum-postcode-script';
            script.async = true;
            script.onload = () => {
                // Script loaded
            };
            document.head.appendChild(script);
        }
    };

    useEffect(() => {
        loadDaumPostcodeScript();
    }, []);

    useEffect(() => {
        // Initialize postcode when modal is shown
        if (showPostcode && window.daum && window.daum.Postcode) {
            initPostcode();
        }
    }, [showPostcode]);

    const initPostcode = () => {
        if (!postcodeContainerRef.current) return;

        new window.daum.Postcode({
            oncomplete: handlePostcodeComplete,
            width: '100%',
            height: '100%'
        }).embed(postcodeContainerRef.current);
    };

    const handlePostcodeComplete = (data) => {
        setFormData(prev => ({
            ...prev,
            zipCode: data.zonecode,
            address1: data.roadAddress || data.jibunAddress
        }));
        setShowPostcode(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };


    const requestEmailVerification = async () => {
        if (!formData.email) {
            setErrors(prev => ({ ...prev, email: '이메일을 입력해주세요' }));
            return;
        }

        try {
            // 인증 코드 요청 API 호출
            const response = await useApi.post('/auth/email_verify', {
                email: `${formData.email}@cau.ac.kr`,
                name: formData.name
            });

            // 응답 처리
            if (response.data && response.data.result === 'SUCCESS') {
                setVerifyButtonEnabled(true);
                alert('인증 코드가 이메일로 전송되었습니다. 메일함을 확인해주세요.');
            } else {
                const errorMessage = response.data && response.data.message
                    ? response.data.message
                    : '이메일 인증 요청에 실패했습니다.';
                alert(errorMessage);
            }
        } catch (error) {
            console.error('Email verification request error:', error);

            // 오류 응답 처리
            if (error.response && error.response.data) {
                alert(error.response.data.message || '이메일 인증 요청 중 오류가 발생했습니다.');
            } else {
                alert('서버 연결에 실패했습니다. 잠시 후 다시 시도해주세요.');
            }
        }
    };

    const verifyEmailCode = async () => {
        if (!emailVerificationCode) {
            alert('인증 코드를 입력해주세요.');
            return;
        }

        try {
            // 이메일 인증 코드 검증 API 호출
            const response = await useApi.post('/auth/verify_code', {
                email: `${formData.email}@cau.ac.kr`,
                code: emailVerificationCode
            });

            // 응답 처리
            if (response.data && response.data.result === 'SUCCESS') {
                alert('이메일이 성공적으로 인증되었습니다.');
                setIsEmailVerified(true);
                setVerifyButtonEnabled(false); // 인증 완료 시 버튼 비활성화
            } else {
                const errorMessage = response.data && response.data.message
                    ? response.data.message
                    : '인증 코드가 일치하지 않습니다.';
                alert(errorMessage);
            }
        } catch (error) {
            console.error('Email verification error:', error);

            // 오류 응답 처리
            if (error.response && error.response.data) {
                alert(error.response.data.message || '이메일 인증 확인 중 오류가 발생했습니다.');
            } else {
                alert('서버 연결에 실패했습니다. 잠시 후 다시 시도해주세요.');
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Name validation
        if (!formData.name.trim()) {
            newErrors.name = '이름을 입력해주세요';
        }

        // ID validation
        if (!formData.userId.trim()) {
            newErrors.userId = '아이디를 입력해주세요';
        } else if (!/^[a-zA-Z0-9]{4,16}$/.test(formData.userId)) {
            newErrors.userId = '아이디는 4~16자의 영문 또는 숫자여야 합니다';
        }

        // Password validation
        if (!formData.password) {
            newErrors.password = '비밀번호를 입력해주세요';
        } else if (formData.password.length < 4 || formData.password.length > 12) {
            newErrors.password = '비밀번호는 4~12자여야 합니다';
        } else if (!/^[a-zA-Z0-9]+$/.test(formData.password)) {
            newErrors.password = '비밀번호는 영문자와 숫자만 사용 가능합니다';
        }

        // Password confirmation
        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = '비밀번호가 일치하지 않습니다';
        }

        // Address validation
        if (!formData.zipCode || !formData.address1) {
            newErrors.address = '우편번호 찾기를 통해 주소를 입력해주세요';
        }

        if (!formData.address2) {
            newErrors.address2 = '상세 주소를 입력해주세요';
        }

        // Phone validation
        if (!formData.phone1 || !formData.phone2 || !formData.phone3) {
            newErrors.phone = '연락처를 모두 입력해주세요';
        } else if (!/^\d{2,3}$/.test(formData.phone1) || !/^\d{3,4}$/.test(formData.phone2) || !/^\d{4}$/.test(formData.phone3)) {
            newErrors.phone = '유효한 연락처를 입력해주세요';
        }

        // Email validation
        if (!formData.email) {
            newErrors.email = '포탈 이메일을 입력해주세요';
        }
        // else if (!isEmailVerified) {
        //     newErrors.email = '이메일 인증이 필요합니다';
        // }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            // Send registration request to the server
            const response = await useApi.post('/user/register', {
                name: formData.name,
                userId: formData.userId,
                password: formData.password,
                zipCode: formData.zipCode,
                address1: formData.address1,
                address2: formData.address2,
                phone: `${formData.phone1}-${formData.phone2}-${formData.phone3}`,
                email: formData.email,
                backupEmail: formData.backupEmail || null
            });

            if (response.data.result === 'SUCCESS') {
                alert('회원가입이 완료되었습니다. 로그인 페이지로 이동합니다.');
                navigate('/intro/login');
            } else {
                alert(response.data.message || '회원가입에 실패했습니다.');
            }
        } catch (error) {
            console.error('Registration error:', error);
            alert('회원가입 처리 중 오류가 발생했습니다.');
        }
    };

    const handleCancel = () => {
        if (window.confirm('회원가입을 취소하시겠습니까?')) {
            navigate('/intro/login');
        }
    };

    return (
        <Wrapper>
            <FormTitle>회원가입</FormTitle>

            {/* Main Registration Form */}
            <form onSubmit={handleSubmit}>
                <FormTable>
                    <tbody>
                    <TableRow>
                        <TableHeader>이름</TableHeader>
                        <TableCell>
                            <Input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="이름을 입력하세요"
                            />
                            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableHeader>아이디</TableHeader>
                        <TableCell>
                            <Input
                                type="text"
                                name="userId"
                                value={formData.userId}
                                onChange={handleChange}
                                placeholder="4~16자의 영문 또는 숫자"
                            />
                            {errors.userId && <ErrorMessage>{errors.userId}</ErrorMessage>}
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableHeader>비밀번호</TableHeader>
                        <TableCell>
                            <Input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="4~12자의 영문/숫자 조합"
                            />
                            {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableHeader>비밀번호 확인</TableHeader>
                        <TableCell>
                            <Input
                                type="password"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                placeholder="비밀번호를 다시 입력하세요"
                            />
                            {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword}</ErrorMessage>}
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableHeader>주소</TableHeader>
                        <TableCell>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                <Input
                                    type="text"
                                    name="zipCode"
                                    value={formData.zipCode}
                                    readOnly
                                    width="100px"
                                    placeholder="우편번호"
                                />
                                <AddressButton type="button" onClick={() => setShowPostcode(true)}>
                                    우편번호 찾기
                                </AddressButton>
                            </div>

                            <Input
                                type="text"
                                name="address1"
                                value={formData.address1}
                                readOnly
                                placeholder="기본주소"
                                style={{ marginBottom: '5px' }}
                            />

                            <Input
                                type="text"
                                name="address2"
                                value={formData.address2}
                                onChange={handleChange}
                                placeholder="상세주소를 입력하세요"
                            />

                            {errors.address && <ErrorMessage>{errors.address}</ErrorMessage>}
                            {errors.address2 && <ErrorMessage>{errors.address2}</ErrorMessage>}
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableHeader>연락처</TableHeader>
                        <TableCell>
                            <PhoneContainer>
                                <Input
                                    type="text"
                                    name="phone1"
                                    value={formData.phone1}
                                    onChange={handleChange}
                                    width="70px"
                                    maxLength={3}
                                    placeholder="010"
                                />
                                <span>-</span>
                                <Input
                                    type="text"
                                    name="phone2"
                                    value={formData.phone2}
                                    onChange={handleChange}
                                    width="70px"
                                    maxLength={4}
                                    placeholder="0000"
                                />
                                <span>-</span>
                                <Input
                                    type="text"
                                    name="phone3"
                                    value={formData.phone3}
                                    onChange={handleChange}
                                    width="70px"
                                    maxLength={4}
                                    placeholder="0000"
                                />
                            </PhoneContainer>
                            {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableHeader>포탈 이메일</TableHeader>
                        <TableCell>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Input
                                    type="text"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    width="40%"
                                    disabled={isEmailVerified}
                                    placeholder="포탈 아이디"
                                />
                                <span style={{ margin: '0 5px' }}>@cau.ac.kr</span>
                                <AddressButton
                                    type="button"
                                    onClick={requestEmailVerification}
                                    disabled={isEmailVerified}
                                >
                                    인증 요청
                                </AddressButton>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                                <Input
                                    type="text"
                                    value={emailVerificationCode}
                                    onChange={(e) => setEmailVerificationCode(e.target.value)}
                                    width="40%"
                                    placeholder="인증 코드 입력"
                                    disabled={isEmailVerified}
                                />
                                <AddressButton
                                    type="button"
                                    onClick={verifyEmailCode}
                                    disabled={isEmailVerified}
                                >
                                    인증 확인
                                </AddressButton>
                            </div>

                            {isEmailVerified && (
                                <p style={{ color: 'green', fontSize: '14px', marginTop: '5px' }}>이메일이 인증되었습니다.</p>
                            )}

                            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableHeader>예비 이메일</TableHeader>
                        <TableCell>
                            <Input
                                type="email"
                                name="backupEmail"
                                value={formData.backupEmail}
                                onChange={handleChange}
                                placeholder="비밀번호 찾기 등에 사용됩니다 (선택사항)"
                            />
                            <HelpText>비밀번호 찾기에 사용됩니다. (선택사항)</HelpText>
                        </TableCell>
                    </TableRow>
                    </tbody>
                </FormTable>

                <ButtonContainer>
                    <CancelButton type="button" onClick={handleCancel}>취소</CancelButton>
                    <SubmitButton type="submit">가입하기</SubmitButton>
                </ButtonContainer>
            </form>

            {/* Daum Postcode Modal */}
            {showPostcode && (
                <PostcodeModal onClick={() => setShowPostcode(false)}>
                    <PostcodeContainer onClick={(e) => e.stopPropagation()}>
                        <CloseButton onClick={() => setShowPostcode(false)}>×</CloseButton>
                        <div ref={postcodeContainerRef} style={{ width: '100%', height: '100%' }} />
                    </PostcodeContainer>
                </PostcodeModal>
            )}
        </Wrapper>
    );
};

export default Signup;